import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';
import type { ApiEnvelope, FeeBasis, ShippingAgent, ShippingAgentFee, ShippingAgentOffice, ShippingAgentRate, ShippingMode } from '../types/Procurement';

/**
 * Shipping agents are global reference data, readable in every project (the
 * project id in the URL is checked but does not scope them). Agents carry
 * `rates` and `fees`; every money field is a decimal string with a `currency`
 * beside it, and a write that sends an amount without a currency is a 400.
 * A contractor reads the same agents with the money fields null.
 */

const enc = encodeURIComponent;

async function unwrap<T>(promise: Promise<ApiEnvelope<T>>): Promise<T> {
  const res = await promise;
  if (!res || res.success === false) throw new Error(res?.error || 'The request did not go through.');
  return res.data;
}

export interface ShippingAgentWritableFields {
  slug: string;
  name: string;
  shipping_mode: ShippingMode;
  active: boolean;
  currency: string | null;
  rate_per_cbm: string | null;
  rate_per_kg: string | null;
  packing_fee: string | null;
  packing_fee_threshold_cbm: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  tracking_url: string | null;
  notes: string | null;
  offices: ShippingAgentOffice[];
}

export type CreateShippingAgentInput = Partial<ShippingAgentWritableFields> & Pick<ShippingAgentWritableFields, 'slug' | 'name'>;
export type UpdateShippingAgentInput = Partial<ShippingAgentWritableFields>;

export interface RateInput {
  destination: string;
  office_label?: string | null;
  shipping_mode?: ShippingMode;
  min_chargeable_cbm?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  confirmed_by?: string | null;
  notes?: string | null;
  currency?: string | null;
  rate_per_cbm?: string | null;
  rate_per_kg?: string | null;
  pickup_fee?: string | null;
}

export interface FeeInput {
  rate_id?: string | null;
  fee_type: string;
  label?: string | null;
  charge_basis: FeeBasis;
  applies_below_cbm?: string | null;
  applies_above_cbm?: string | null;
  office_label?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  notes?: string | null;
  currency?: string | null;
  amount?: string | null;
}

/** Every agent, inactive ones included, each with its rates and fees. */
export async function loadAllShippingAgents(): Promise<ShippingAgent[]> {
  const data = await unwrap(apiGet<ApiEnvelope<ShippingAgent[]>>(`${planningPath('/shipping-agents')}?include_inactive=true`));
  return Array.isArray(data) ? data : [];
}

/** One agent. There is no single-agent route; the list is small, so it is picked out of the list. */
export async function loadShippingAgent(id: string): Promise<ShippingAgent | null> {
  return (await loadAllShippingAgents()).find(a => a.id === id) ?? null;
}

export const createShippingAgent = (input: CreateShippingAgentInput) =>
  unwrap(apiPost<ApiEnvelope<ShippingAgent>>(planningPath('/shipping-agents'), input));

export const updateShippingAgent = (id: string, input: UpdateShippingAgentInput) =>
  unwrap(apiPut<ApiEnvelope<ShippingAgent>>(planningPath(`/shipping-agents/${enc(id)}`), input));

/** An agent in use cannot be deleted (409 agent_in_use): it is switched off instead. */
export const archiveShippingAgent = (id: string) => updateShippingAgent(id, { active: false });
export const restoreShippingAgent = (id: string) => updateShippingAgent(id, { active: true });

export async function deleteShippingAgent(id: string): Promise<void> {
  await apiDelete(planningPath(`/shipping-agents/${enc(id)}`));
}

export const createRate = (agentId: string, input: RateInput) =>
  unwrap(apiPost<ApiEnvelope<ShippingAgentRate>>(planningPath(`/shipping-agents/${enc(agentId)}/rates`), input));
export const updateRate = (agentId: string, rateId: string, input: Partial<RateInput>) =>
  unwrap(apiPut<ApiEnvelope<ShippingAgentRate>>(planningPath(`/shipping-agents/${enc(agentId)}/rates/${enc(rateId)}`), input));
export async function deleteRate(agentId: string, rateId: string): Promise<void> {
  await apiDelete(planningPath(`/shipping-agents/${enc(agentId)}/rates/${enc(rateId)}`));
}

export const createFee = (agentId: string, input: FeeInput) =>
  unwrap(apiPost<ApiEnvelope<ShippingAgentFee>>(planningPath(`/shipping-agents/${enc(agentId)}/fees`), input));
export const updateFee = (agentId: string, feeId: string, input: Partial<FeeInput>) =>
  unwrap(apiPut<ApiEnvelope<ShippingAgentFee>>(planningPath(`/shipping-agents/${enc(agentId)}/fees/${enc(feeId)}`), input));
export async function deleteFee(agentId: string, feeId: string): Promise<void> {
  await apiDelete(planningPath(`/shipping-agents/${enc(agentId)}/fees/${enc(feeId)}`));
}
